import { Button, Chip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import React from "react";

// Shared look for the card-based order listings (lab and pharmacy)

export const ORDER_COLORS = {
  primary: "#098963",
  dark: "#022219",
  muted: "#828584",
  border: "#CEEEE5",
  light: "#E7F6F2",
};

// Chip colours per status; unknown statuses fall back to neutral grey
export const getStatusStyle = (status: string) => {
  const value = (status || "").trim().toLowerCase();
  if (["completed", "delivered", "fulfilled", "report ready", "reportready", "paid", "success"].includes(value)) {
    return { color: "#098963", bg: "#E7F6F2" };
  }
  if (["cancelled", "canceled", "failed", "rejected"].includes(value)) {
    return { color: "#D32F2F", bg: "#FDECEA" };
  }
  if (["refund", "refunded", "refund initiated", "refundinitiated"].includes(value)) {
    return { color: "#7B1FA2", bg: "#F3E5F5" };
  }
  if ([
    "pending", "confirmed", "sample collected", "samplecollected", "in progress", "inprogress",
    "packed", "shipped", "out for delivery", "outfordelivery", "ready to dispatch",
  ].includes(value)) {
    return { color: "#B26A00", bg: "#FFF4E5" };
  }
  return { color: "#55595A", bg: "#F1F2F2" };
};

export const formatDate = (value: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// yyyy-mm-dd, comparable with <input type="date"> values
export const toDateKey = (value: string) => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

export const formatAmount = (value: number) => `₹${(Number(value) || 0).toLocaleString("en-IN")}`;

export const StatusChip: React.FC<{ label: string }> = ({ label }) => {
  const style = getStatusStyle(label);
  return (
    <Chip
      size="small"
      label={label || "N/A"}
      sx={{ bgcolor: style.bg, color: style.color, fontWeight: 600, borderRadius: "6px", height: 24 }}
    />
  );
};

// Red blinking border for an order that just arrived (until the vendor opens it)
export const newOrderBlinkSx = {
  border: "2px solid #D32F2F",
  animation: "newOrderBlink 1s ease-in-out infinite",
  "@keyframes newOrderBlink": {
    "0%, 100%": { borderColor: "#D32F2F", boxShadow: "0 0 0 0 rgba(211, 47, 47, 0.6)" },
    "50%": { borderColor: "rgba(211, 47, 47, 0.2)", boxShadow: "0 0 14px 4px rgba(211, 47, 47, 0.55)" },
  },
  // No flashing for people who ask for less motion: a steady red glow instead
  "@media (prefers-reduced-motion: reduce)": { animation: "none", boxShadow: "0 0 0 3px rgba(211, 47, 47, 0.35)" },
};

// Small label shown on a blinking card, so it is not only colour / motion that marks it
export const NewOrderTag: React.FC = () => (
  <Chip size="small" label="NEW" sx={{ bgcolor: "#D32F2F", color: "#fff", fontWeight: 700, height: 20 }} />
);

// First action on a new order; accepting it stops the blinking and the siren
export const AcceptOrderButton: React.FC<{ onAccept: () => void }> = ({ onAccept }) => (
  <Button
    fullWidth
    variant="contained"
    disableElevation
    startIcon={<CheckIcon />}
    onClick={(e) => {
      e.stopPropagation();
      onAccept();
    }}
    sx={{
      bgcolor: ORDER_COLORS.primary,
      color: "#fff",
      borderRadius: "10px",
      textTransform: "none",
      fontWeight: 700,
      "&:hover": { bgcolor: "#077352" },
    }}
  >
    Accept
  </Button>
);

export const orderFieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };

export const orderFilterChipSx = (selected: boolean) => ({
  fontWeight: 600,
  borderRadius: "8px",
  bgcolor: selected ? ORDER_COLORS.primary : "#fff",
  color: selected ? "#fff" : ORDER_COLORS.dark,
  border: `1px solid ${selected ? ORDER_COLORS.primary : ORDER_COLORS.border}`,
  "&:hover": { bgcolor: selected ? ORDER_COLORS.primary : ORDER_COLORS.light },
});
